import { selectOrgConfig } from 'core/selectors/org';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import { addCommas } from 'core/utils/helpers/numbers';

const hasRebateElevation = ({ originalValue, value }) =>
  typeof originalValue !== 'undefined' && originalValue !== value;

const chooseRebateCurrencyVersion = (currencyLong, currency = '%', useLong) => {
  if (!currencyLong || currencyLong.length === 0) {
    return currency;
  }

  return useLong ? currencyLong : currency;
};

const chooseRebateValueCurrencyVersion = ({ currencyLong, currency }, useLong) =>
  chooseRebateCurrencyVersion(currencyLong, currency, useLong);

const chooseRebateOriginalValueCurrencyVersion = ({ originalCurrencyLong, originalCurrency }, useLong) =>
  chooseRebateCurrencyVersion(originalCurrencyLong, originalCurrency, useLong);

const combineValueWithLanguage = (value, currencyLanguage) => {
  if (!currencyLanguage) return value;
  // Handle space between the value and currency:
  // No space needed if currencyLanguage starts with a single character ('2x', '5x bonus points', '5%')
  // Add space if currencyLanguage starts with multiple characters ('2 miles/$', '5 bonus points')
  return /^\S\s|^\S$/.test(currencyLanguage) ? `${value}${currencyLanguage}` : `${value} ${currencyLanguage}`;
};

const mergedRebateValueWithCurrency = (value, currency, currencyLong, useLong) => {
  const currencyLanguage = useLong ? currencyLong : currency;

  if (currencyLanguage === '$') {
    // If current is dollar put sign it before value without whitespace
    // If value is not a whole number make sure two decimals are shown
    /* eslint-disable-next-line no-nested-ternary */
    return currencyLanguage + (isNumber(value) ? (Number.isInteger(value) ? value : value.toFixed(2)) : value);
  }

  return combineValueWithLanguage(value, currencyLanguage);
};

const extractRebateCurrencyPrefix = (currency) => {
  const currencyPieces = currency.split('/');
  return currencyPieces[0] || currency;
};

const extractRebateValueCurrencyPrefix = ({ currency, currencyLong }, useLong) =>
  extractRebateCurrencyPrefix(useLong ? currencyLong : currency);

const extractRebateOriginalValueCurrencyPrefix = ({ originalCurrency, originalCurrencyLong }, useLong) =>
  extractRebateCurrencyPrefix(useLong ? originalCurrencyLong : originalCurrency);

const extractRebateCurrencySuffix = ({ currency }) => {
  const currencyPieces = currency.split('/');
  return currencyPieces.length > 1 ? `/${currencyPieces[1]}` || '' : '';
};

// could have some future parsing logic here of OCAPI prefix field - keep it simple and return the 'up to' text for now
const extractTieredPrefix = isTiered => (isTiered ? 'up to' : '');

const extractEarnPrefix = (isTiered, prefix) =>
  (isTiered ? prefix && prefix.substr(0, prefix.indexOf('up to')).trim() : prefix);

const parseBaseRebate = (rebate, state) => {
  if (!rebate) {
    return null;
  }

  const { useCurrencyLong, useOriginalCurrencyLong } = selectOrgConfig(state);

  rebate.currency = rebate.currency || '';
  rebate.originalCurrency = rebate.originalCurrency || '';

  const {
    value, originalValue, currency, currencyLong, originalCurrencyLong, originalCurrency,
  } = rebate;

  const formattedValue = addCommas(value);
  const formattedOriginalValue = addCommas(originalValue);

  return {
    elevation: hasRebateElevation(rebate),
    currency: {
      value: {
        current: chooseRebateValueCurrencyVersion(rebate, useCurrencyLong),
        original: chooseRebateOriginalValueCurrencyVersion(rebate, useOriginalCurrencyLong),
      },
      prefix: {
        current: extractRebateValueCurrencyPrefix(rebate, useCurrencyLong),
        original: extractRebateOriginalValueCurrencyPrefix(rebate, useOriginalCurrencyLong),
      },
      suffix: extractRebateCurrencySuffix(rebate),
    },
    originalValueWithCurrency: mergedRebateValueWithCurrency(
      originalValue, originalCurrency, originalCurrencyLong, useOriginalCurrencyLong,
    ),
    valueWithCurrency: mergedRebateValueWithCurrency(value, currency, currencyLong, useCurrencyLong),
    formattedValue,
    formattedOriginalValue,
    formattedOriginalValueWithCurrency: mergedRebateValueWithCurrency(
      formattedOriginalValue, originalCurrency, originalCurrencyLong, useOriginalCurrencyLong,
    ),
    formattedValueWithCurrency: mergedRebateValueWithCurrency(formattedValue, currency, currencyLong, useCurrencyLong),
  };
};

const parseTiers = (tiers, state) => {
  if (isEmpty(tiers)) {
    return [];
  }
  return tiers.map(tier => merge(tier, { rebate: parseBaseRebate(tier.rebate, state) }));
};

export const parseRebate = (rebate, tiers, state, isDeactivated) => {
  if (isDeactivated) {
    const parsedRebate = {
      isDeactivated: true,
      value: 'No reward',
    };
    return parsedRebate;
  }

  if (!rebate) {
    return null;
  }

  const parsedRebate = parseBaseRebate(rebate, state);

  parsedRebate.tiers = parseTiers(tiers, state);
  parsedRebate.isTiered = rebate.prefix && rebate.prefix.indexOf('up to') !== -1;
  parsedRebate.tieredPrefix = extractTieredPrefix(parsedRebate.isTiered);
  parsedRebate.earnPrefix = extractEarnPrefix(parsedRebate.isTiered, rebate.prefix);

  return parsedRebate;
};

export default parseRebate;
